import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  Version,
  AuditFilters,
  AuditStats,
  PaginatedVersions,
  OrderItemChanges,
  ArchivedVersion,
  PaginatedArchivedVersions,
  ArchiveStats
} from 'shared';

@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private readonly baseUrl = '/audit';

  constructor(private http: HttpClient) {}

  /**
   * Get paginated versions with optional filters
   */
  getVersions(filters: AuditFilters = {}): Observable<PaginatedVersions> {
    let params = new HttpParams();

    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof AuditFilters];
      if (value !== undefined && value !== null) {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<PaginatedVersions>(`${this.baseUrl}/versions`, { params });
  }

  /**
   * Get single version details
   */
  getVersion(id: number): Observable<Version> {
    return this.http.get<Version>(`${this.baseUrl}/versions/${id}`);
  }

  /**
   * Get complete history for a specific user
   * @param userId - User ID to fetch history for
   * @param includeRelated - Include related records (orders, transactions)
   * @param startDate - Optional start date filter
   * @param endDate - Optional end date filter
   */
  getUserHistory(
    userId: number,
    includeRelated: boolean = true,
    startDate?: string,
    endDate?: string
  ): Observable<{ data: Version[] }> {
    let params = new HttpParams()
      .set('include_related', includeRelated.toString());

    if (startDate) {
      params = params.set('start_date', startDate);
    }
    if (endDate) {
      params = params.set('end_date', endDate);
    }

    return this.http.get<{ data: Version[] }>(
      `${this.baseUrl}/users/${userId}/history`,
      { params }
    );
  }

  /**
   * Get history for specific model/record
   */
  getModelHistory(itemType: string, itemId: number): Observable<{ data: Version[] }> {
    return this.http.get<{ data: Version[] }>(
      `${this.baseUrl}/models/${itemType}/${itemId}`
    );
  }

  /**
   * Get order item changes for a specific order
   */
  getOrderItemChanges(orderId: number): Observable<OrderItemChanges> {
    return this.http.get<OrderItemChanges>(
      `${this.baseUrl}/orders/${orderId}/item_changes`
    );
  }

  /**
   * Get user pool changes for a specific user
   * @param userId - User ID to fetch pool changes for
   * @param poolId - Optional pool ID filter (1-5)
   * @param startDate - Optional start date filter
   * @param endDate - Optional end date filter
   */
  getUserPoolChanges(
    userId: number,
    poolId?: number,
    startDate?: string,
    endDate?: string
  ): Observable<{ user_id: number; total_pool_changes: number; changes: Version[] }> {
    let params = new HttpParams();

    if (poolId !== undefined) {
      params = params.set('pool_id', poolId.toString());
    }
    if (startDate) {
      params = params.set('start_date', startDate);
    }
    if (endDate) {
      params = params.set('end_date', endDate);
    }

    return this.http.get<{ user_id: number; total_pool_changes: number; changes: Version[] }>(
      `${this.baseUrl}/users/${userId}/pool_changes`,
      { params }
    );
  }

  /**
   * Get audit statistics
   */
  getStats(): Observable<AuditStats> {
    return this.http.get<AuditStats>(`${this.baseUrl}/stats`);
  }

  // ==========================================
  // ClickHouse Archive Methods (Cold Storage)
  // ==========================================

  /**
   * Get archived versions from ClickHouse (older than 90 days)
   */
  getArchivedVersions(filters: AuditFilters = {}): Observable<PaginatedArchivedVersions> {
    let params = new HttpParams();

    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof AuditFilters];
      if (value !== undefined && value !== null) {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<PaginatedArchivedVersions>(`${this.baseUrl}/archive`, { params });
  }

  /**
   * Get archive statistics from ClickHouse
   */
  getArchiveStats(): Observable<{ data: ArchiveStats; source: string }> {
    return this.http.get<{ data: ArchiveStats; source: string }>(`${this.baseUrl}/archive/stats`);
  }

  /**
   * Manually trigger archive job
   * @param dryRun - If true, only preview what would be archived
   */
  runArchiveJob(dryRun: boolean = false): Observable<{ data?: any; message: string }> {
    return this.http.post<{ data?: any; message: string }>(
      `${this.baseUrl}/archive/run`,
      { dry_run: dryRun }
    );
  }

  /**
   * Get user-friendly event label
   */
  getEventLabel(event: string): string {
    switch (event) {
      case 'create': return 'Created';
      case 'update': return 'Updated';
      case 'destroy': return 'Deleted';
      default: return event;
    }
  }

  /**
   * Get user-friendly model name
   */
  getModelLabel(modelType: string): string {
    switch (modelType) {
      case 'User': return 'User';
      case 'Order': return 'Order';
      case 'OrderDetail': return 'Order Item';
      case 'EwalletTransaction': return 'E-Wallet Transaction';
      case 'Bill': return 'Bill';
      case 'UserPool': return 'User Pool';
      case 'AccountBalance': return 'Account Balance';
      case 'UserPoolTurnover': return 'Pool Turnover';
      case 'Payout': return 'Payout';
      case 'PoolSwap': return 'Pool Swap';
      case 'PayoutApiToken': return 'Payout API Token';
      case 'PayoutBatchCode': return 'Payout Batch Code';
      case 'ExchangeRate': return 'Exchange Rate';
      case 'WithdrawalRequest': return 'Withdrawal Request';
      case 'Product': return 'Product';
      case 'CardAccount': return 'Card Account';
      case 'CardTransaction': return 'Card Transaction';
      case 'WpcpRule': return 'WPCP Rule';
      case 'WpcpCategory': return 'WPCP Category';
      case 'WpcpSetting': return 'WPCP Setting';
      default: return modelType;
    }
  }
}
